import * as React from 'react';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {OnboardingBody} from 'shells/onboarding-page/onboarding-body';
import {Body} from 'shells/typography/body';

import type {CalendarSync} from 'nutshell-graphql-types';

import {CalendarWidgetGoogle} from '../../../setup/calendar/calendar-widget-google';
import {CalendarWidgetOffice} from '../../../setup/calendar/calendar-widget-office';
import {useSetGoogleCalendarSyncIsEnabled} from '../../../setup/calendar/graphql/hooks/use-set-google-calendar-sync-is-enabled';
import {useSetOffice365CalendarSyncIsEnabled} from '../../../setup/calendar/graphql/hooks/use-set-office365-calendar-sync-is-enabled';

import {analyticsClient} from '../../../../analytics-client';
import {appHistory} from '../../../../history';

import {ConnectedCalendarInfoDescription} from '../connected-calendar-info-description';
import {SelectCalendarDropdown} from '../select-calendar-dropdown';

import styles from './connected.module.css';

type Props = {
    calendarSync: CalendarSync | null | undefined;
    isLoading: boolean;
    refetchSyncStatus: () => Promise<any>;
    setIsEditModalOpen?: (isEditModalOpen: boolean) => void;
    oneStepDisconnect?: boolean;
    canDisconnect?: boolean;
};

export function Connected(props: Props) {
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);

    const {setGoogleCalendarSyncIsEnabled} = useSetGoogleCalendarSyncIsEnabled();
    const {setOffice365CalendarSyncIsEnabled} = useSetOffice365CalendarSyncIsEnabled();

    const {calendarSync} = props;

    if (props.isLoading) {
        return (
            <div className='absolutely-center-and-fill'>
                <LoadingPage />
            </div>
        );
    }

    if (!calendarSync) {
        // TODO: From the meeting page, this is returning here now causing abrupt modal closure
        return null;
    }
    const {isEnabled, username, externalService, syncErrors} = calendarSync;

    const calendarWidgetProps = {
        isEnabled: isEnabled,
        statusMessage: 'Connected',
        errors: syncErrors || [],
        username: username,
        onAuthStart: () => {
            setErrorMessages([]);
        },
        onAuthSuccess() {
            analyticsClient.track(analyticsClient.EVENTS.TRACK_CONNECTED_CALENDAR_VIA_SCHEDULER);

            return props.refetchSyncStatus();
        },
        // @ts-expect-error
        onAuthError: (errMsg) => {
            setErrorMessages(errorMessages.concat(errMsg));
        },
        onlyDisable: true,
        dialogButtonText: 'Disable',
        revokeWarning:
            'Are you sure you want to disable this calendar connection? People will not be able to schedule meetings with you until you enable it again or connect to another calendar.',
        location: 'top',
        onRevokeClick: () => {
            props.setIsEditModalOpen?.(false);
            if (externalService === 'OFFICE365') {
                setOffice365CalendarSyncIsEnabled(false);
            } else {
                setGoogleCalendarSyncIsEnabled(false);
            }
        },
    } as const;

    if (externalService === 'GOOGLE' || externalService === 'GOOGLE_APPS') {
        return (
            <OnboardingBody title='Your Google Calendar is connected'>
                <StackLegacy spacing={8}>
                    <div className={styles['calendar-spacer']}>
                        <CalendarWidgetGoogle
                            {...calendarWidgetProps}
                            externalService={externalService}
                            appHistory={appHistory}
                        />
                    </div>
                    <Body>Select which calendar you want to use with Scheduler:</Body>
                    <SelectCalendarDropdown status={calendarSync} maxMenuHeight={175} />
                </StackLegacy>
                <ConnectedCalendarInfoDescription />
            </OnboardingBody>
        );
    }

    if (externalService === 'OFFICE365') {
        return (
            <OnboardingBody title='Your Office365 Calendar is connected'>
                <StackLegacy spacing={8}>
                    <div className={styles['calendar-spacer']}>
                        <CalendarWidgetOffice {...calendarWidgetProps} appHistory={appHistory} />
                    </div>
                    <Body>Select which calendar you want to use with Scheduler:</Body>
                    <SelectCalendarDropdown status={calendarSync} maxMenuHeight={175} />
                </StackLegacy>
                <ConnectedCalendarInfoDescription />
            </OnboardingBody>
        );
    }

    return null;
}
